import { useEffect, useMemo, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import SearchableSelect from '@/components/common/SearchableSelect';
import { useDateFormat } from '@/hooks/useDateFormat';
import { useProcessConfiguration } from '@/hooks/useProcessConfiguration';
import type { AvailableBatchDto } from '@/types/production';
import type {
  CreateJobWorkIssueRequest, JobWorkIssueDto, JobWorkIssueLineInput, JobWorkOrderDto, JobWorkPickListDto,
} from '@/types/jobWork';

interface LineState {
  qty: number;
  scannedBarcode: string;
  locationId: string | null;
  batchId: string | null;
  // Barcode mode only — true once this line's own physical label has been scanned. Also drives the
  // scanned-rows-to-top ordering, same pattern as the Production Material Issue screen.
  checked: boolean;
}

/** Mobile-first single-column layout, same shape as Production Material Issue — this is a
 * shop-floor screen used while physically handing goods to the vendor's driver.
 *
 * Works off the pick list generated when the order was Released: with Barcode Required ON the rows
 * are one-per-physical-label and each must be scanned; with it OFF they're one-per-material with
 * the FIFO-resolved location, and quantities are typed in. Both rules are re-validated server-side
 * in JobWorkIssueService.CreateAsync. */
export default function JobWorkIssuePage() {
  const { t } = useTranslation('jobWork');
  const { formatDate } = useDateFormat();
  const queryClient = useQueryClient();

  const { data: orders = [] } = useQuery({
    queryKey: ['job-work-orders'],
    queryFn: async () => (await apiClient.get<JobWorkOrderDto[]>('/job-work-orders')).data,
  });
  const { data: issues = [], isLoading: loadingIssues } = useQuery({
    queryKey: ['job-work-issues'],
    queryFn: async () => (await apiClient.get<JobWorkIssueDto[]>('/job-work-issues')).data,
  });

  const { data: config } = useProcessConfiguration();
  const barcodeMandatory = config?.barcodeMandatory ?? false;

  // Only Released, still-open orders can take an issue — the same gate the backend enforces.
  const issuableOrders = orders.filter((o) => o.status === 'Released' && !o.isClosed);

  const [orderId, setOrderId] = useState('');
  const [lineState, setLineState] = useState<Record<string, LineState>>({});
  const [availableBatches, setAvailableBatches] = useState<Record<string, AvailableBatchDto[]>>({});
  // The one shared scan input for barcode mode — replaces a per-line scan field. Typing/scanning a
  // value matching a not-yet-checked line's barcode auto-checks it and clears for the next scan.
  const [scanInput, setScanInput] = useState('');
  const [scanOrder, setScanOrder] = useState<string[]>([]);
  const [outwardDcNo, setOutwardDcNo] = useState('');
  const [vehicleNo, setVehicleNo] = useState('');
  const [remarks, setRemarks] = useState('');
  const [error, setError] = useState<string | null>(null);

  // The order's pick list, generated on Release — or generated on the spot for an order released
  // before pick lists existed (see JobWorkPickListService.GetOrGenerateForOrderAsync).
  const { data: pickList = null } = useQuery({
    queryKey: ['job-work-pick-list', orderId],
    queryFn: async () => (await apiClient.get<JobWorkPickListDto>(`/job-work-issues/pick-list/${orderId}`)).data,
    enabled: Boolean(orderId),
  });

  // Seeds the per-line editing state once the pick list arrives (it loads asynchronously after the
  // order is chosen, so this can't live in the order-select handler).
  useEffect(() => {
    if (!pickList) {
      setLineState({});
      return;
    }
    setScanOrder([]);
    setLineState(
      Object.fromEntries(
        pickList.lines.map((l) => [
          l.id,
          {
            qty: Math.max(l.requiredQty - l.pickedQty, 0),
            scannedBarcode: '',
            locationId: l.locationId,
            batchId: l.batchId,
            // Barcode mode starts every line unconfirmed (must be scanned); with it off there's
            // nothing to scan against, so every line is already ready.
            checked: !barcodeMandatory,
          },
        ]),
      ),
    );
    // barcodeMandatory deliberately omitted — a background config refetch shouldn't wipe in-progress
    // scan state while the operator is working.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickList]);

  // A line the generator couldn't resolve (shortfall row) needs the operator to choose from what's
  // actually on hand — fetched lazily per distinct material.
  useEffect(() => {
    if (!pickList) return;
    pickList.lines
      .filter((l) => !l.locationId)
      .forEach((l) => {
        if (availableBatches[l.materialId]) return;
        apiClient
          .get<AvailableBatchDto[]>('/job-work-issues/available-batches', {
            params: { materialId: l.materialId, warehouseId: pickList.warehouseId },
          })
          .then((res) => setAvailableBatches((prev) => ({ ...prev, [l.materialId]: res.data })));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickList]);

  const selectOrder = (id: string) => {
    setOrderId(id);
    setError(null);
    setScanInput('');
    setScanOrder([]);
  };

  const selectedOrder = issuableOrders.find((o) => o.id === orderId) ?? null;

  // Tries to match `value` against a not-yet-checked line's barcode — on every keystroke (a scanner
  // types the full value near-instantly) and explicitly on Enter (fallback for a scanner that sends
  // Enter, or manual typing).
  const tryMatchScan = (value: string): boolean => {
    const trimmed = value.trim().toUpperCase();
    if (!trimmed || !pickList) return false;
    const matchLine = pickList.lines.find((l) => !lineState[l.id]?.checked && (l.barcode ?? '').toUpperCase() === trimmed);
    if (!matchLine) return false;
    setLineState((prev) => ({ ...prev, [matchLine.id]: { ...prev[matchLine.id], checked: true, scannedBarcode: value.trim() } }));
    setScanOrder((prev) => [matchLine.id, ...prev.filter((id) => id !== matchLine.id)]);
    setScanInput('');
    return true;
  };

  const handleScanInputChange = (value: string) => {
    if (!tryMatchScan(value)) setScanInput(value);
  };

  const handleScanKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      tryMatchScan(scanInput);
    }
  };

  // Scanned lines float to the top (most-recent first); everything else keeps its original order.
  const orderedLines = useMemo(() => {
    if (!pickList) return [];
    const scannedFirst = scanOrder.map((id) => pickList.lines.find((l) => l.id === id)).filter((l): l is NonNullable<typeof l> => Boolean(l));
    const rest = pickList.lines.filter((l) => !scanOrder.includes(l.id));
    return [...scannedFirst, ...rest];
  }, [pickList, scanOrder]);

  const issueMutation = useMutation({
    mutationFn: () => {
      const lines: JobWorkIssueLineInput[] = Object.entries(lineState)
        .filter(([, s]) => s.qty > 0)
        .map(([jobWorkPickListLineId, s]) => ({
          jobWorkPickListLineId,
          qty: s.qty,
          scannedBarcode: s.scannedBarcode || null,
          locationId: s.locationId,
          batchId: s.batchId,
        }));
      const payload: CreateJobWorkIssueRequest = {
        jobWorkPickListId: pickList!.id,
        lines,
        outwardDcNo: outwardDcNo || null,
        vehicleNo: vehicleNo || null,
        remarks: remarks || null,
      };
      return apiClient.post('/job-work-issues', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job-work-issues'] });
      queryClient.invalidateQueries({ queryKey: ['job-work-orders'] });
      queryClient.invalidateQueries({ queryKey: ['job-work-pick-list', orderId] });
      setOrderId('');
      setLineState({});
      setOutwardDcNo('');
      setVehicleNo('');
      setRemarks('');
      setError(null);
    },
    onError: (e: unknown) => {
      const message = (e as { response?: { data?: { errors?: string[] } } })?.response?.data?.errors?.[0];
      setError(message ?? t('issue.genericError'));
    },
  });

  const totalToIssue = useMemo(
    () => Object.values(lineState).reduce((sum, s) => sum + (s.qty > 0 ? s.qty : 0), 0),
    [lineState],
  );

  // Only enforced while Barcode Required is on — the backend re-validates this exact rule.
  const allBarcodesReady = useMemo(() => {
    if (!pickList) return false;
    if (!barcodeMandatory) return true;
    return pickList.lines.every((l) => {
      const s = lineState[l.id];
      if (!s || s.qty <= 0) return true;
      return l.barcode ? s.checked : false;
    });
  }, [pickList, lineState, barcodeMandatory]);

  const scannedCount = pickList?.lines.filter((l) => (lineState[l.id]?.qty ?? 0) > 0 && l.barcode && lineState[l.id]?.checked).length ?? 0;
  const scannableCount = pickList?.lines.filter((l) => (lineState[l.id]?.qty ?? 0) > 0 && l.barcode).length ?? 0;

  const historyColumns: DataTableColumn<JobWorkIssueDto>[] = [
    { field: 'issueNumber', headerName: t('issue.table.number') },
    { field: 'jobWorkOrderNumber', headerName: t('issue.table.order'), filterable: true },
    { field: 'supplierName', headerName: t('issue.table.vendor'), filterable: true },
    { field: 'warehouseName', headerName: t('issue.table.warehouse'), filterable: true },
    { field: 'issueDate', headerName: t('issue.table.date'), render: (r) => formatDate(r.issueDate) },
    { field: 'outwardDcNo', headerName: t('issue.table.dcNo'), render: (r) => r.outwardDcNo ?? '—' },
    { field: 'issuedByUserName', headerName: t('issue.table.issuedBy') },
    { field: 'lines', headerName: t('issue.table.lines'), render: (r) => r.lines.length },
  ];

  return (
    <>
      <PageHeader title={t('issue.title')} subtitle={t('issue.subtitle')} />

      <Box sx={{ maxWidth: 560, mx: { xs: 0, sm: 'auto' } }}>
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <SearchableSelect
                label={t('issue.selectOrder')} value={orderId} onChange={selectOrder} fullWidth size="medium"
                helperText={issuableOrders.length === 0 ? t('issue.noOrdersHint') : undefined}
                options={issuableOrders.map((o) => ({ value: o.id, label: `${o.orderNumber} — ${o.supplierName}` }))}
              />

              {error && <Alert severity="error">{error}</Alert>}

              {selectedOrder && (
                <Alert severity="info" sx={{ py: 0.5 }}>
                  {t('issue.outputHint', {
                    material: `${selectedOrder.outputMaterialCode} — ${selectedOrder.outputMaterialName}`,
                    qty: selectedOrder.plannedQty,
                  })}
                </Alert>
              )}

              {pickList && (
                <Alert severity="info" sx={{ py: 0.5 }}>
                  {barcodeMandatory
                    ? t('issue.barcodeModeHint', { pickList: pickList.pickListNumber })
                    : t('issue.manualModeHint', { pickList: pickList.pickListNumber })}
                </Alert>
              )}

              {pickList && barcodeMandatory && (
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <TextField
                    label={t('issue.scanBarcode')}
                    value={scanInput}
                    onChange={(e) => handleScanInputChange(e.target.value)}
                    onKeyDown={handleScanKeyDown}
                    helperText={t('issue.scanBarcodeHelper')}
                    fullWidth
                    autoFocus
                  />
                  <Chip
                    label={t('issue.scannedOfTotal', { scanned: scannedCount, total: scannableCount })}
                    color={allBarcodesReady ? 'success' : 'warning'}
                    sx={{ mt: 1.25, fontWeight: 700, minWidth: 92, flexShrink: 0 }}
                  />
                </Stack>
              )}

              {pickList && (
                <Stack spacing={1.5}>
                  {(barcodeMandatory ? orderedLines : pickList.lines).map((line) => {
                    const s = lineState[line.id];
                    const remaining = Math.max(line.requiredQty - line.pickedQty, 0);
                    const needsLocationChoice = !line.locationId;
                    const candidates = availableBatches[line.materialId] ?? [];
                    const scanned = barcodeMandatory && Boolean(s?.checked) && (s?.qty ?? 0) > 0;
                    return (
                      <Box
                        key={line.id}
                        sx={{
                          p: 1.5, border: 1, borderRadius: 1,
                          borderColor: scanned ? 'success.main' : 'divider',
                          bgcolor: scanned ? 'success.50' : undefined,
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                          <Stack direction="row" spacing={1} alignItems="flex-start">
                            {barcodeMandatory && line.barcode && (
                              // Read-only — selection is scan-driven only (see the Scan Barcode
                              // field above); manually ticking a line is deliberately not possible.
                              <Checkbox size="small" checked={Boolean(s?.checked)} disabled sx={{ mt: -0.5, ml: -1 }} />
                            )}
                            <Box>
                              <Typography variant="subtitle2">{line.materialCode}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {line.materialName}
                              </Typography>
                              <Typography variant="caption" display="block" color="text.secondary">
                                {line.locationCode ? `${t('issue.location')}: ${line.locationCode} · ` : ''}
                                {line.batchNo ? `${t('issue.batch')}: ${line.batchNo} · ` : ''}
                                {t('issue.qtyToSend')}: {remaining}
                                {line.packingQty ? ` (${line.packingQty} ${t('issue.packingQty')})` : ''}
                              </Typography>
                            </Box>
                          </Stack>
                          <Chip size="small" label={t('issue.pending', { qty: remaining })} color={remaining > 0 ? 'warning' : 'success'} />
                        </Stack>

                        {needsLocationChoice && (
                          <SearchableSelect
                            label={t('issue.selectLocation')} fullWidth size="small" sx={{ mb: 1.5 }}
                            value={s?.locationId ? `${s.locationId}|${s.batchId ?? ''}` : ''}
                            onChange={(next) => {
                              const [locationId, batchId] = next.split('|');
                              setLineState((prev) => ({ ...prev, [line.id]: { ...prev[line.id], locationId: locationId || null, batchId: batchId || null } }));
                            }}
                            options={candidates.map((c) => ({
                              value: `${c.locationId ?? ''}|${c.batchId ?? ''}`,
                              label: `${c.locationCode ?? '—'} · ${c.batchNo ?? t('issue.noBatch')} · ${c.quantityAvailable}`,
                            }))}
                          />
                        )}

                        {barcodeMandatory && (
                          line.barcode ? (
                            <Typography
                              variant="caption" display="block" sx={{ mb: 1.5 }}
                              color={s?.checked ? 'success.main' : 'text.secondary'}
                            >
                              {t('issue.expectedBarcode', { barcode: line.barcode })}
                              {s?.checked ? ` — ${t('issue.scanned')}` : ''}
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="error" display="block" sx={{ mb: 1.5 }}>
                              {t('issue.noBarcodeBlocked')}
                            </Typography>
                          )
                        )}

                        {barcodeMandatory ? (
                          // Fixed to this row's own label quantity — a scanned physical unit is
                          // issued in full, not partially, so there's nothing to edit here.
                          <TextField
                            type="number" label={t('issue.qtyToSend')} fullWidth size="medium"
                            value={s?.qty ?? 0}
                            slotProps={{ htmlInput: { inputMode: 'decimal', style: { fontSize: '1.1rem' }, readOnly: true } }}
                          />
                        ) : (
                          <TextField
                            type="number" label={t('issue.qtyToSend')} fullWidth size="medium"
                            value={s?.qty ?? 0}
                            onChange={(e) => setLineState((prev) => ({ ...prev, [line.id]: { ...prev[line.id], qty: Number(e.target.value) } }))}
                            slotProps={{ htmlInput: { inputMode: 'decimal', style: { fontSize: '1.1rem' } } }}
                          />
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              )}

              {pickList && (
                <>
                  <TextField label={t('issue.outwardDcNo')} value={outwardDcNo} onChange={(e) => setOutwardDcNo(e.target.value)} fullWidth />
                  <TextField label={t('issue.vehicleNo')} value={vehicleNo} onChange={(e) => setVehicleNo(e.target.value)} fullWidth />
                  <TextField label={t('issue.remarks')} value={remarks} onChange={(e) => setRemarks(e.target.value)} fullWidth multiline minRows={2} />
                </>
              )}

              <Divider />

              <Button
                variant="contained" size="large" fullWidth
                disabled={!orderId || !pickList || totalToIssue <= 0 || !allBarcodesReady || issueMutation.isPending}
                onClick={() => issueMutation.mutate()}
              >
                {issueMutation.isPending ? t('issue.issuing') : t('issue.issueButton')}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Box>

      <Box sx={{ mt: 4 }}>
        <Typography variant="h5" sx={{ mb: 1.5 }}>
          {t('issue.historyTitle')}
        </Typography>
        <DataTable columns={historyColumns} rows={issues} loading={loadingIssues} searchable getRowId={(r) => r.id} exportFileName="job-work-issues" />
      </Box>
    </>
  );
}
